import { useState, useEffect } from 'react';
import { X, Share, Smartphone } from 'lucide-react';
import { shouldShowInstallBanner, dismissInstallBanner } from '@/utils/pwaDetection';
import { InstallInstructions } from './InstallInstructions';

/**
 * iOS Safari Install Banner
 *
 * Shows a dismissable banner prompting iOS Safari users to add the app
 * to their Home Screen for the full PWA experience with push notifications.
 */
export function InstallBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Check if we should show the banner after a short delay
    // This prevents the banner from flashing on page load
    const timer = setTimeout(() => {
      setIsVisible(shouldShowInstallBanner());
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    dismissInstallBanner();
  };

  const handleShowInstructions = () => {
    setShowInstructions(true);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Banner positioned above navigation */}
      <div
        className="fixed bottom-[calc(var(--nav-total-height,60px)+8px)] left-4 right-4 z-50 animate-slideUp"
        role="alert"
        aria-live="polite"
      >
        <div className="bg-gradient-to-r from-[#8e4585] to-[#a85a9f] rounded-xl shadow-lg p-4 text-white">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="shrink-0 p-2 bg-white/20 rounded-lg">
              <Smartphone size={24} className="text-white" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-white text-sm">Install Spirit of Shabbat</h3>
              <p className="text-white/90 text-xs mt-0.5">
                Add to Home Screen for the best experience and notifications
              </p>

              {/* Action button */}
              <button
                onClick={handleShowInstructions}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-[#8e4585] rounded-lg text-xs font-medium hover:bg-white/90 transition-colors"
              >
                <Share size={14} />
                Show me how
              </button>
            </div>

            {/* Dismiss button */}
            <button
              onClick={handleDismiss}
              className="shrink-0 p-1.5 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Dismiss install prompt"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Instructions bottom sheet */}
      <InstallInstructions
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        onDismissBanner={handleDismiss}
      />

      {/* Animation styles */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
